import { reportError } from './reportError.ts';

/* A size every video encoder takes; at one frame a second it costs nothing. */
const PICTURE_WIDTH = 160;
const PICTURE_HEIGHT = 120;

/**
 * Keeps the computer from idle-sleeping, also in a hidden tab, until the returned
 * function is called. Chrome holds a system wake lock for a live peer connection,
 * Safari for a playing video with sound and a remote picture: so two local peers
 * send each other a blank picture, played with a silent track. Nothing leaves.
 */
export function keepSystemAwake(): () => void {
  if (typeof RTCPeerConnection === 'undefined') return () => {};

  const undo: (() => void)[] = [];
  let released = false;
  const release = () => {
    if (released) return;
    released = true;
    undo.forEach((step) => step());
  };
  const fail = (caught: unknown) => {
    /* Released halfway: whatever was closed refuses the next step. */
    if (released) return;
    reportError(caught, { what: 'keep-system-awake' });
    release();
  };

  try {
    const caller = new RTCPeerConnection({ iceServers: [] });
    undo.push(() => caller.close());
    const callee = new RTCPeerConnection({ iceServers: [] });
    undo.push(() => callee.close());

    /* A candidate that does not take only means one path fewer between the two. */
    caller.onicecandidate = ({ candidate }) => {
      if (candidate) void callee.addIceCandidate(candidate).catch(() => {});
    };
    callee.onicecandidate = ({ candidate }) => {
      if (candidate) void caller.addIceCandidate(candidate).catch(() => {});
    };
    caller.createDataChannel('awake');
    playBlankPicture(caller, callee, undo);

    void (async () => {
      const offer = await caller.createOffer();
      await caller.setLocalDescription(offer);
      await callee.setRemoteDescription(offer);
      const answer = await callee.createAnswer();
      await callee.setLocalDescription(answer);
      await caller.setRemoteDescription(answer);
    })().catch(fail);
  } catch (caught) {
    fail(caught);
  }

  return release;
}

/**
 * Safari's half. Without it the connection still keeps Chrome awake, so a
 * browser that cannot draw or play this only gets reported.
 */
function playBlankPicture(
  caller: RTCPeerConnection,
  callee: RTCPeerConnection,
  undo: (() => void)[],
): void {
  try {
    const canvas = document.createElement('canvas');
    canvas.width = PICTURE_WIDTH;
    canvas.height = PICTURE_HEIGHT;
    const context = canvas.getContext('2d');
    if (context === null) return;

    /* A canvas only hands out a frame when it changes. */
    let filled = false;
    const paint = () => {
      filled = !filled;
      if (filled) context.fillRect(0, 0, PICTURE_WIDTH, PICTURE_HEIGHT);
      else context.clearRect(0, 0, PICTURE_WIDTH, PICTURE_HEIGHT);
    };
    paint();
    const [picture] = canvas.captureStream(1).getVideoTracks();
    const repaint = setInterval(paint, 1000);
    undo.push(() => {
      clearInterval(repaint);
      picture.stop();
    });
    caller.addTrack(picture);

    /*
     * Safari pauses a silent video in a hidden tab, unless it plays a stream
     * with sound while the page holds a microphone; an empty Web Audio track is
     * sound enough and nobody hears it.
     */
    const audio = new AudioContext();
    undo.push(() => void audio.close().catch(() => {}));
    const silence = audio.createMediaStreamDestination().stream.getAudioTracks();

    const video = document.createElement('video');
    video.playsInline = true;
    undo.push(() => {
      video.pause();
      video.srcObject = null;
    });
    callee.ontrack = ({ track }) => {
      video.srcObject = new MediaStream([track, ...silence]);
      /* Refused or cut short: Chrome is still kept awake by the connection. */
      void video.play().catch(() => {});
    };
  } catch (caught) {
    reportError(caught, { what: 'keep-system-awake-picture' });
  }
}
